import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { FC } from 'react';
import { Add, Edit } from '@mui/icons-material';
import { StyledPaper } from '../../theme';
import { Guide } from '../Details';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CREATE_NEW_GUIDE_CONTENT_DOCUMENT } from './content.graphql';
import { Modal, useModal } from '../../dialogs/useModal';
import { TiptapDocumentInfo } from './TiptapDocumentInfo';

interface InfoBoxConfirmationResult {
  confirm: boolean;
  comment?: string;
}

export function InfoBoxConfirmation({ modal }: { modal: Modal<{}, InfoBoxConfirmationResult> }) {
  const handleCancel = () => modal.close({ confirm: false });
  const [comment, setComment] = useState('');
  return (
    <Dialog onClose={handleCancel} maxWidth="sm" fullWidth open={true}>
      <DialogTitle>Er du sikker?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Box>Hvis du fortsetter vil du miste muligheten til å endre på eksisterende innhold i CMSet.</Box>
          <Box sx={{ mt: 2 }}>Dette er noe man vanligvis bare gjør ved starten av en fullstendig revisjon av innholdet.</Box>
          <TextField
            label="Kommentar"
            fullWidth
            placeholder="Beskriv hvorfor du ønsker å starte på et nytt dokument"
            onChange={(e) => {
              setComment(e.target.value);
            }}
            multiline
            minRows={2}
            maxRows={4}
            value={comment}></TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Avbryt</Button>
        <Button variant="contained" color="primary" onClick={() => modal.close({ confirm: true, comment })} autoFocus>
          Start på nytt med blanke ark
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const InfoBox: FC<{ guide: Guide }> = ({ guide }) => {
  const navigate = useNavigate();
  const [createNewContentDocument, { loading: isCreatingNewContentDocument }] = useMutation(CREATE_NEW_GUIDE_CONTENT_DOCUMENT);
  const createNewDocumentConfirmModal = useModal<{}, InfoBoxConfirmationResult>({ data: {} });

  const handleCreateNewCmsContentDocument = async () => {
    var result = await createNewDocumentConfirmModal.open();
    if (!result || !result.confirm) return;

    await createNewContentDocument({ variables: { input: { id: guide.id, comment: result.comment } } });
    navigate(`/guide-editor/${guide.id}/edit`);
  };

  const showCreateNewDocumentButton = !!guide.tiptapDocument && guide.tiptapDocument.documentVersion > -1;

  return (
    <StyledPaper>
      <Typography variant="h6" component="h3">
        Innhold
      </Typography>
      {guide.tiptapDocument && <TiptapDocumentInfo tiptapDocument={guide.tiptapDocument} />}
      <Stack sx={{ mt: 2 }} direction="row" spacing={2}>
        <Button variant="contained" component={Link} to={`/guide-editor/${guide.id}/edit`} startIcon={<Edit />}>
          Endre i CMS
        </Button>

        {showCreateNewDocumentButton && (
          <Button variant="outlined" disabled={isCreatingNewContentDocument} onClick={handleCreateNewCmsContentDocument} startIcon={<Add />}>
            Start på nytt med blanke ark
          </Button>
        )}
      </Stack>
      {createNewDocumentConfirmModal.isOpen && <InfoBoxConfirmation modal={createNewDocumentConfirmModal} />}
    </StyledPaper>
  );
};
